import Clarity from "@microsoft/clarity";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { CLARITY_PROJECT_ID } from "../../project_config";
import { api } from "../../utils/api";
import { LoginHelper } from "../../utils/LoginHelper";
import CatalogEmptyState from "../common/catalog/CatalogEmptyState";
import Breadcrumb from "../common/breadcrumb/Breadcrumb";
import Footer from "../common/footer/Footer";
import Header from "../common/header/Header";
import Loading from "../common/loading/Loading";
import MySubscriptionRow from "./MySubscriptionRow";
import SubscriptionPlanCard from "./SubscriptionPlanCard";

export default function SubscriptionList() {
	const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
	const [plans, setPlans] = useState([]);
	const [subscriptions, setSubscriptions] = useState([]);
	const [error, setError] = useState("");
	const [loadingPlan, setLoadingPlan] = useState(null);
	const [isLoadingPlans, setIsLoadingPlans] = useState(true);

	useEffect(() => {
		Clarity.init(CLARITY_PROJECT_ID);
		const resize = () => setIsDesktop(window.innerWidth > 768);
		window.addEventListener("resize", resize);

		setIsLoadingPlans(true);
		api
			.subscriptionList({})
			.then((list) => {
				setPlans(list ?? []);
				setIsLoadingPlans(false);
			})
			.catch((err) => {
				setError(err?.data?.message || "دریافت پلن‌ها ناموفق بود");
				setIsLoadingPlans(false);
			});

		if (LoginHelper.isLoggedIn()) {
			api
				.subscriptionMyList({ token: LoginHelper.getToken() })
				.then((list) => setSubscriptions(list ?? []))
				.catch(() => {});
		}

		return () => window.removeEventListener("resize", resize);
	}, []);

	function purchase(plan) {
		if (!LoginHelper.isLoggedIn()) {
			window.location.href = "/login/";
			return;
		}
		setLoadingPlan(plan.id);
		setError("");
		api
			.subscriptionPreparePurchase({
				token: LoginHelper.getToken(),
				plan_id: plan.id,
			})
			.then((result) => {
				window.location.href = result.gateway_url;
			})
			.catch((err) => {
				setError(err?.data?.message || "شروع پرداخت ناموفق بود");
				setLoadingPlan(null);
			});
	}

	const linkDataList = [
		{ label: "خانه", href: "/" },
		{ label: "اشتراک‌ها", href: "/subscriptions/" },
	];

	return (
		<>
			<Helmet>
				<title>خرید اشتراک | همراهتیم</title>
				<meta name="description" content="انتخاب و خرید پلن اشتراک" />
			</Helmet>
			<div className="bg-[#F0F6FC] min-h-screen flex flex-col justify-between">
				<div>
					<Header isDesktop={isDesktop} />
					<div className="bg-white">
						<div
							className={`${isDesktop ? "w-[1200px]" : ""} mx-auto`}
						>
							<Breadcrumb linkDataList={linkDataList} />
						</div>
					</div>

					<main
						className={`${isDesktop ? "max-w-[1200px]" : "px-[10px]"} mx-auto w-full flex flex-col gap-6 pt-6 md:pt-8 pb-10`}
					>
						<section
							className="relative overflow-hidden rounded-[16px] border border-darkBlue/10 bg-gradient-to-br from-white via-lightCta/80 to-white shadow-[0_10px_28px_rgba(39,106,166,0.1)]"
							aria-label="پلن‌های اشتراک"
						>
							<div
								className="h-[3px] w-full bg-gradient-to-l from-cta via-[#FF8A6B] to-darkBlue"
								aria-hidden
							/>
							<div className="px-5 py-6 md:py-7 text-center">
								<h1 className="font-Vazir-700 text-[20px] md:text-[22px] text-darkBlue">
									پلن‌های اشتراک
								</h1>
								<p className="mt-2 max-w-lg mx-auto font-Vazir-400 text-[14px] text-darkBlue/55 leading-relaxed">
									پلن مناسب خود را انتخاب کنید و اعتبار استفاده از
									محتوای اشتراکی دریافت کنید.
								</p>
							</div>
						</section>

						{error && (
							<div className="rounded-[14px] border border-red-200 bg-white px-4 py-4 text-center font-Vazir-400 text-[14px] text-[#cc0000]">
								{error}
							</div>
						)}

						{isLoadingPlans && (
							<div className="flex justify-center py-12">
								<Loading color="#ff694b" width={44} />
							</div>
						)}

						{!isLoadingPlans && plans.length >= 1 && (
							<div
								className={[
									"mx-auto grid w-full items-stretch gap-5",
									"[&>*]:h-full [&>*]:min-w-0",
									isDesktop
										? plans.length === 1
											? "max-w-[440px] grid-cols-1"
											: plans.length === 2
												? "max-w-[920px] grid-cols-2"
												: "max-w-[1200px] grid-cols-3"
										: "max-w-[440px] grid-cols-1",
								].join(" ")}
							>
								{plans.map((plan) => (
									<SubscriptionPlanCard
										key={plan.id}
										plan={plan}
										isLoading={loadingPlan === plan.id}
										onPurchase={purchase}
									/>
								))}
							</div>
						)}

						{!isLoadingPlans && plans.length === 0 && !error && (
							<CatalogEmptyState
								message="در حال حاضر پلن فعالی برای خرید وجود ندارد."
								hint="به‌زودی پلن‌های جدید اضافه می‌شوند."
							/>
						)}

						{LoginHelper.isLoggedIn() && subscriptions.length > 0 && (
							<section
								className="rounded-[16px] border border-darkBlue/10 bg-white p-5 md:p-6 shadow-[0_6px_20px_rgba(39,106,166,0.06)]"
								aria-label="اشتراک‌های من"
							>
								<h2 className="font-Vazir-700 text-[17px] text-darkBlue text-center mb-5 pb-3 border-b border-darkBlue/10">
									اشتراک‌های من
								</h2>
								<div className="flex flex-col gap-3">
									{subscriptions.map((item) => (
										<MySubscriptionRow key={item.id} item={item} />
									))}
								</div>
							</section>
						)}

						{LoginHelper.isLoggedIn() &&
							subscriptions.length === 0 &&
							!isLoadingPlans &&
							plans.length > 0 && (
								<p className="text-center font-Vazir-400 text-[13px] text-darkBlue/45">
									هنوز اشتراک فعالی ندارید؛ یک پلن انتخاب کنید.
								</p>
							)}
					</main>
				</div>
				<Footer isDesktop={isDesktop} />
			</div>
		</>
	);
}
